import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DEFAULT_LOCALE } from "@/i18n";
import { ICandidateAssessment } from "@/modules/assessments/assessments.types";
import { ILocalizedTestElement, ITestGroupHeader, LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import { ITestAnswer } from "@/shared/components/charts/types/chartTypes";
import { ITestChoice } from "@/shared/components/candidate/scenes/TestElement/TestElement";

import { AssessmentHttpService } from "@/modules/assessments/services/assessment-http.service";
import { InviteHttpService } from "@/modules/assessments/services/invite-http.service";
import { InvitesHttpService } from "@/modules/candidate/services/InvitesHttpService";
import { useReloadToken } from "./useReloadToken.hook";

export function useGetAssessmentWithQuestionsAndAnswersHook({
  inviteId,
  assessmentId,
  isAllowedToLoad = true,
  locale,
}: {
  inviteId?: number;
  assessmentId?: number;
  isAllowedToLoad?: boolean;
  // Forces the Q&A content locale. The admin report passes en-US so candidate content stays English
  // for admins; the candidate flow passes the unconfirmed pick, else the confirmed invite locale.
  locale?: LanguagesIsoEnum;
}) {
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState<ICandidateAssessment | undefined>();
  const [questionsList, setQuestionsList] = useState<ILocalizedTestElement[][]>([]);
  const [answers, setAnswers] = useState<ITestAnswer[]>([]);
  const [groupHeaders, setGroupHeaders] = useState<ITestGroupHeader[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  // Which locale the content currently in state was fetched for; null until the first success. A
  // failure leaves it behind, which is what keeps `isContentStale` true rather than silently letting
  // the previous locale's content pass for the requested one.
  const [loadedContent, setLoadedContent] = useState<{ locale?: LanguagesIsoEnum } | null>(null);
  const { reloadToken, retry } = useReloadToken();

  useEffect(() => {
    if (!isAllowedToLoad) {
      setIsLoading(false);
      return;
    }

    // Stale-response guard: if this run is superseded (e.g. locale changes at Confirm) before its
    // fetch resolves, skip the setState so an earlier in-flight request can't clobber the newer data.
    let cancelled = false;

    const fetchAllData = async () => {
      if (!inviteId || !assessmentId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Only the assessment read defaults the locale: the Q&A endpoint is invite-scoped and resolves
        // from invite.locale on the BE when none is sent.
        const [assessmentData, questionsAndAnswersData] = await Promise.all([
          AssessmentHttpService.getCandidateAssessmentByID({ assessmentId, locale: locale ?? DEFAULT_LOCALE }),
          InviteHttpService.getQuestionsAndAnswersByInviteId(inviteId, locale),
        ]);

        if (cancelled) return;

        setAssessment(assessmentData);
        setQuestionsList(questionsAndAnswersData.questions);
        setAnswers(questionsAndAnswersData.answers);
        setGroupHeaders(questionsAndAnswersData.groupHeaders ?? []);
        setLoadedContent({ locale });
        setError(null);
      } catch (e: any) {
        if (!cancelled) {
          console.error("Error fetching assessment data:", e);
          setError(e);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchAllData();

    return () => {
      cancelled = true;
    };
  }, [isAllowedToLoad, inviteId, assessmentId, locale, reloadToken]);

  const setAnswer = (testAnswer: ITestAnswer) =>
    setAnswers((prevState) => {
      const filteredAnswers = prevState.filter(({ testId }) => testAnswer.testId !== testId);

      return [...filteredAnswers, testAnswer];
    });

  const saveTestElementAnswer = async (
    currentAnswers: ITestChoice[],
    { inviteId, testId }: { inviteId: number; testId: number },
    options: { isLast?: boolean; isBack?: boolean; skipAndRandomize?: boolean } = {
      isBack: false,
      isLast: false,
      skipAndRandomize: false,
    },
  ) => {
    const addedAnswer = await InvitesHttpService.addTestElementAnswer(
      inviteId,
      testId,
      {
        isLast: options.isLast,
        isBack: options.isBack,
        skipAndRandomize: options.skipAndRandomize,
        answers: currentAnswers,
      },
      navigate,
    );

    setAnswer(addedAnswer);

    return addedAnswer;
  };

  return {
    assessment,
    questionsList,
    answers,
    groupHeaders,
    isLoading,
    error,
    // True whenever what's on screen isn't the requested locale's content — from the render in which
    // `locale` changes, through the re-fetch, and onwards if that re-fetch fails. Callers gate Start on
    // it so a candidate can't begin in the language they just switched away from.
    isContentStale: isAllowedToLoad && (loadedContent === null || loadedContent.locale !== locale),
    retry,
    setAnswer,
    saveTestElementAnswer,
  };
}
